import { useTranslation } from 'react-i18next'

import ParrotMark from '../common/ParrotMark'
import { LEVEL_LABELS } from '../../lib/home'
import { faDigits } from '../../lib/numerals'
import { TONE, avatarById } from '../../lib/shop'
import { daysLeft, previewMembers, teamCardState, teamName } from '../../lib/teams'
import UserLink from './UserLink'
import UserPhoto from './UserPhoto'

function TeamGlyph() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="9" r="2.6" />
      <path d="M15.5 14.2A4.6 4.6 0 0 1 21 18.5" />
    </svg>
  )
}

/**
 * A member's shop avatar as the feed draws an author: the parrot on the
 * avatar's tone — or their profile photo once photos are on (V5-w;
 * `photoUrl` is null for everyone until then).
 */
export function MemberDisc({ avatar, photoUrl = null, size = 34, ring = false }) {
  const tone = TONE[avatarById(avatar).tone] ?? TONE.brand
  return (
    <UserPhoto photoUrl={photoUrl} size={size} ring={ring}>
      <span
        className={`grid shrink-0 place-items-center rounded-full border-2 ${tone.disc} ${tone.ring} ${ring ? 'ring-2 ring-surface' : ''}`}
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        <ParrotMark size={Math.round(size * 0.57)} />
      </span>
    </UserPhoto>
  )
}

function PeriodLine({ periodEndsAt }) {
  const { t } = useTranslation('teams')
  const days = daysLeft(periodEndsAt)
  if (days === null) return null
  return <span className="text-[11.5px] text-ink-3">{days <= 1 ? t('lastDay') : t('daysLeft', { days: faDigits(days) })}</span>
}

/**
 * The learner's team (V4-w), from the `teams_mine` answer. Renders nothing
 * while the answer is missing, the call failed, or teams are switched off —
 * so until `teams.enabled` is turned on, home and profile look exactly as
 * they did. `compact` is the home card (name and an avatar row); the
 * profile shows every member with their level and streak. A teammate is
 * shown with what the feed shows of any author, never a phone or full name.
 */
export default function TeamCard({ answer, compact = false }) {
  const { t, i18n } = useTranslation('teams')
  const state = teamCardState(answer)
  if (state === 'hidden') return null

  if (state === 'none') {
    return (
      <section className="flex items-start gap-3 rounded-2xl border border-dashed border-line bg-surface p-3.5" data-team-state="none">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-2 text-ink-3">
          <TeamGlyph />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-sm font-semibold text-ink">{t('none.title')}</span>
          <span className="text-[12px] leading-[1.8] text-ink-2">{t('none.body')}</span>
        </span>
      </section>
    )
  }

  const { team, periodEndsAt } = answer
  const name = teamName(team, i18n.language)

  const header = (
    <div className="flex items-center gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600">
        <TeamGlyph />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[11.5px] font-semibold text-ink-2">{t('heading')}</span>
        <span className="truncate text-[15.5px] font-bold text-ink">{name}</span>
      </span>
      <span className="shrink-0 rounded-[9px] bg-surface-2 px-2.5 py-1.5 text-[11.5px] font-semibold text-ink-2">
        {t('members', { n: faDigits(team.members.length) })}
      </span>
    </div>
  )

  if (compact) {
    const { shown, more } = previewMembers(team.members)
    return (
      <section className="space-y-3 card-soft p-3.5" data-team-state="team">
        {header}
        <div className="flex items-center justify-between gap-2">
          <ul className="flex -space-x-2" aria-label={t('ariaMembers')}>
            {shown.map((member) => (
              <li key={member.id} title={member.me ? t('you') : member.displayName}>
                <UserLink userId={member.id} className="block rounded-full" label={member.me ? t('you') : member.displayName}>
                  <MemberDisc avatar={member.avatar} photoUrl={member.photoUrl} size={32} ring />
                </UserLink>
              </li>
            ))}
            {more > 0 && (
              <li className="grid size-8 place-items-center rounded-full bg-surface-2 text-[11px] font-semibold text-ink-2 ring-2 ring-surface">
                {t('more', { n: faDigits(more) })}
              </li>
            )}
          </ul>
          <PeriodLine periodEndsAt={periodEndsAt} />
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-3 card-soft p-3.5" data-team-state="team">
      {header}
      <ul className="space-y-2" aria-label={t('ariaMembers')}>
        {team.members.map((member) => (
          <li key={member.id} className={`flex items-center gap-3 rounded-xl px-2 py-1.5 ${member.me ? 'bg-brand-50' : ''}`}>
            <UserLink userId={member.id} className="shrink-0 rounded-full" label={member.displayName}>
              <MemberDisc avatar={member.avatar} photoUrl={member.photoUrl} />
            </UserLink>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-ink">
                <UserLink userId={member.id}>{member.displayName}</UserLink>
                {member.me && <span className="ms-1.5 text-[11px] font-semibold text-brand-600">({t('you')})</span>}
              </span>
              <span className="text-[11.5px] text-ink-2">
                {LEVEL_LABELS[member.level] ?? member.level} · {t('streak', { days: faDigits(member.streakDays ?? 0) })}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <PeriodLine periodEndsAt={periodEndsAt} />
    </section>
  )
}
